import { FC, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@new/lib';
import { Checkbox } from '@new/common/ui/Checkbox/Checkbox';
import { SCRIPT_CHECKS } from '../../constants';
import type { ScriptCheckItem } from '../../types';

interface ScriptSectionProps {
  title: string;
  checks: ScriptCheckItem[];
  checkedItems: Record<string, boolean>;
  onToggle: (key: string) => void;
}

const ScriptSection: FC<ScriptSectionProps> = ({ title, checks, checkedItems, onToggle }) => {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const toggleExpand = (key: string) => {
    setExpandedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="flex flex-col gap-3">
      <h4 className="[font-size:var(--fs-l3)]! font-bold text-(--neutral-black-NB800)! m-0!">
        {title}
      </h4>
      <div className="space-y-2">
        {checks.map((check, index) => {
          const key = `${title}-${index}`;
          const isExpanded = expandedItems[key];

          return (
            <div key={key} className="border border-neutral-200 rounded-lg overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3">
                <Checkbox value={!!checkedItems[key]} onChange={() => onToggle(key)} />
                <button
                  onClick={() => toggleExpand(key)}
                  className="flex-1 flex items-center justify-between text-left cursor-pointer bg-transparent border-0 p-0"
                >
                  <span className="text-sm text-neutral-700">{check.title}</span>
                  <ChevronDown
                    size={16}
                    className={cn(
                      'text-neutral-400 transition-transform shrink-0 ml-2',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </button>
              </div>
              {isExpanded && (
                <div className="px-4 pb-3 pt-0">
                  <p className="text-sm text-neutral-500 ml-8">{check.text}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ScriptPanel: FC = () => {
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});

  const handleToggle = (key: string) => {
    setCheckedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="flex flex-col gap-6 p-4">
      <ScriptSection
        title="Intro"
        checks={SCRIPT_CHECKS.intro}
        checkedItems={checkedItems}
        onToggle={handleToggle}
      />
      <ScriptSection
        title="Main Body"
        checks={SCRIPT_CHECKS.main}
        checkedItems={checkedItems}
        onToggle={handleToggle}
      />
      <ScriptSection
        title="Outro"
        checks={SCRIPT_CHECKS.outro}
        checkedItems={checkedItems}
        onToggle={handleToggle}
      />
    </div>
  );
};
